import React, { useCallback, useEffect } from 'react';
import { Box, Grid } from 'enova-frontend-components';

import { ActionBarHeight } from '../../../utils/layout';
import { RegistrationCommonStep } from '../../../utils/navigation';
import { DetailingStep } from '../../../components/registering/utils/registerEnums';
import { AdjustParameters } from '../../../components/registering/advisor/adjustParameters';
import Base from '../../../components/registering/steps/base';
import Doors from '../../../components/registering/steps/doors';
import EnergyUsage from '../../../components/registering/steps/energyUsage';
import Floors from '../../../components/registering/steps/floors';
import { Roof } from '../../../components/registering/steps/roof';
import TiltakStep from '../../../components/registering/advisor/tiltak';
import useRegistrationForm from '../useRegistrationForm';
import { useAutoSave } from '../hooks/useAutoSave';
import { ConstructionDensity } from '../../../components/registering/steps/constructionDensity';
import { Windows } from '../../../components/registering/steps/windows';
import { Walls } from '../../../components/registering/steps/walls';
import { ShapeAndArea } from '../../../components/registering/steps/shapeAndArea';
import { Solceller } from '../../registrationV2025/pages/solceller';
import { StepContentContainer } from '../../../components/registering/steps/stepContentContainer';

import EnergyCertificate from './energyCertificate';
import { RegistrationStepper } from './registrationStepper';
import Summary from './summary';

function isDetailingStep(step: RegistrationCommonStep | DetailingStep) {
  return Object.values(DetailingStep).includes(step as DetailingStep);
}

const RegistrationForm = () => {
  const { step, steps, displayErrors, registrationId } = useRegistrationForm();
  const { mutateAutoSave } = useAutoSave();

  const saveRegistration = useCallback((id: string) => mutateAutoSave(id), []);

  useEffect(() => {
    // Don't autosave if step is not included in the detailing step enum
    if (!isDetailingStep(step)) {
      return;
    }

    const autoSaveInterval = setInterval(() => {
      if (!registrationId) {
        return;
      }

      saveRegistration(registrationId);
    }, 30 * 1000);

    return () => clearInterval(autoSaveInterval);
  }, [step, registrationId]);

  if (!steps.includes(step)) {
    return null;
  }

  return (
    <Grid container spacing={8}>
      <Grid item xs={12} md={4} lg={3} position="relative">
        <Box position="sticky" top={85 + ActionBarHeight}>
          <RegistrationStepper />
        </Box>
      </Grid>

      <Grid item xs={12} md={8} lg={9}>
        <StepContentContainer>
          {step === RegistrationCommonStep.ABOUT && <Base />}
          {step === DetailingStep.Bygningsform && <ShapeAndArea />}
          {step === DetailingStep.Vegger && <Walls />}
          {step === DetailingStep.Vinduer && <Windows />}
          {step === DetailingStep.Solceller && <Solceller />}
          {step === DetailingStep.Takkonstruksjon && <Roof displayErrors={displayErrors} />}
          {step === DetailingStep.Ytterdorer && <Doors />}
          {step === DetailingStep.Gulv && <Floors />}
          {step === DetailingStep.Energibruk && <EnergyUsage />}
          {step === DetailingStep.Lekkasjetall && <ConstructionDensity />}
          {step === RegistrationCommonStep.PARAMETERS && <AdjustParameters registrationId={registrationId} />}
          {step === RegistrationCommonStep.MEASURES && <TiltakStep />}
          {step === RegistrationCommonStep.SUMMARY && <Summary />}
          {step === RegistrationCommonStep.ENERGY_CERTIFICATE && <EnergyCertificate />}
        </StepContentContainer>
      </Grid>
    </Grid>
  );
};

export default RegistrationForm;
